import React, { useCallback, useEffect, useState } from "react";
import { UserX, RefreshCw, CheckCircle2 } from "lucide-react";
import { getVacantPeriods } from "../../api/timetable/timetableApi";
import { getVacantPeriodsPortal } from "../../api/timetable/timetablePortalApi";
import { useTimetableLive } from "../../hooks/useTimetableLive";
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Table,
  EmptyState,
  Skeleton,
  Alert,
} from "../ui";
import { useToast } from "../../context/ToastContext";

/**
 * VacantPeriodsPanel — periods where assigned teacher is ABSENT today.
 * Auto-loads via HTTP on mount; stays in sync via Socket.IO `timetable:vacant`.
 */
const vacantColumns = [
  {
    key: "periodNumber",
    header: "Period",
    render: (v) => <span className="font-bold text-neutral-900 dark:text-neutral-100">P{v.periodNumber}</span>,
  },
  {
    key: "time",
    header: "Time",
    render: (v) => (
      <span className="text-xs text-neutral-500 dark:text-neutral-400">
        {v.startTime} - {v.endTime}
      </span>
    ),
  },
  {
    key: "classSection",
    header: "Class & Section",
    render: (v) => (
      <span className="font-medium text-neutral-900 dark:text-neutral-100">
        {v.classId?.className || "—"} - {v.sectionId?.sectionName || "—"}
      </span>
    ),
  },
  {
    key: "subject",
    header: "Subject",
    render: (v) => v.subjectId?.name || "N/A",
  },
  {
    key: "absentTeacher",
    header: "Absent Teacher",
    render: (v) => (
      <span className="font-semibold text-danger-600">{v.staffId?.name || "Absent"}</span>
    ),
  },
];

export default function VacantPeriodsPanel({ isPortal = false, autoFetch = true }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [live, setLive] = useState(false);

  const toast = useToast();
  const [error, setError] = useState(null);

  const fetchVacant = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = isPortal ? await getVacantPeriodsPortal() : await getVacantPeriods();
      const list = Array.isArray(res) ? res : res?.data || res?.vacant || [];
      setRows(list);
      setLastFetchedAt(new Date());
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch vacant periods");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [isPortal]);

  useEffect(() => {
    if (autoFetch) fetchVacant();
  }, [autoFetch, fetchVacant]);

  useTimetableLive({
    isPortal,
    enabled: true,
    onVacant: (list) => {
      setRows(list);
      setLastFetchedAt(new Date());
      setLive(true);
    },
  });

  return (
    <Card>
      <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <UserX className="h-4 w-4 text-danger-600 shrink-0" />
          <div>
            <CardTitle className="text-base font-semibold">Vacant Periods Today</CardTitle>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
              Teachers marked absent — slots needing cover
              {live ? " · Live" : null}
              {lastFetchedAt
                ? ` · Updated ${lastFetchedAt.toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}`
                : null}
            </p>
          </div>
        </div>
        <Button
          type="button"
          variant="outline-primary"
          size="sm"
          onClick={fetchVacant}
          isLoading={loading}
          leftIcon={<RefreshCw className="h-3.5 w-3.5" />}
        >
          Refresh
        </Button>
      </CardHeader>

      {error ? (
        <CardContent className="pt-0">
          <Alert variant="danger" title="Error">
            {error}
          </Alert>
        </CardContent>
      ) : null}

      <CardContent className="p-0">
        {loading && rows.length === 0 ? (
          <div className="p-6 space-y-3">
            <Skeleton height={20} width="40%" />
            <Skeleton height={120} width="100%" />
          </div>
        ) : (
          <Table
            columns={vacantColumns}
            data={rows}
            emptyState={
              <EmptyState
                icon={CheckCircle2}
                title="No vacant periods"
                description="When a teacher is marked absent, their scheduled periods appear here automatically."
              />
            }
          />
        )}
      </CardContent>
    </Card>
  );
}
